'use client';

import { type ReactNode, useState } from 'react';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@kit/ui/drawer';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import Link from '~/(tenant)/_components/tenant-link';
import { pickLocalized } from '../_lib/use-localized';
import { resolveHomeSportVisual } from '../_lib/sport-icons';
import { SportVisualView } from '../_lib/sport-visual-view';
import { useNativePresentation } from '../_lib/use-native-presentation';
import { useNativeTokens } from '../_lib/use-native-tokens';
import { HorizontalCardScroller, SectionHeader } from '../_primitives';
import type { SportsSectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';
import type { NativeSport } from '~/(tenant)/_lib/server/load-native-sports';
import { homeAssetUrl } from '~/(tenant)/_lib/home-asset-url';

const SEE_ALL_THRESHOLD = 4;

export function SportsSection({ config, sports, accountId }: {
  config: SportsSectionConfig;
  sports: NativeSport[];
  accountId: string;
}) {
  const { t, i18n } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!sports.length) return null;

  const title = pickLocalized(config.title, config.title_ar, i18n.language)?.trim() || t('common:nativeHome.sports', { defaultValue: 'Sports' });
  const cards = sports.map((sport) => <SportCard key={sport.id} sport={sport} config={config} accountId={accountId} />);

  return (
    <section style={{ paddingTop: 32 }} data-test="native-sports-section">
      <SectionHeader
        title={title}
        action={sports.length > SEE_ALL_THRESHOLD ? {
          label: <Trans i18nKey="common:seeAll" defaults="All" />,
          onClick: () => setSheetOpen(true),
        } : undefined}
      />
      <HorizontalCardScroller gap={20}>{cards}</HorizontalCardScroller>
      <SportsSheet title={title} open={sheetOpen} onOpenChange={setSheetOpen}>{cards}</SportsSheet>
    </section>
  );
}

function SportsSheet({ title, open, onOpenChange, children }: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  const presentation = useNativePresentation();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-test="native-sports-sheet" className={cn(presentation.className, 'mx-auto max-w-lg')} style={presentation.style}>
        <DrawerHeader className="relative px-6 pe-16 text-center">
          <DrawerTitle className="text-center">{title}</DrawerTitle>
          <DrawerDescription className="sr-only">{title}</DrawerDescription>
          <DrawerClose asChild>
            <Button variant="outline" size="icon" className="absolute end-4 top-2 h-11 w-11 rounded-full">
              <X className="h-5 w-5" />
              <span className="sr-only"><Trans i18nKey="common:close" defaults="Close" /></span>
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="grid grid-cols-3 justify-items-center gap-x-4 gap-y-6 overflow-y-auto px-6 pt-2 pb-10 sm:grid-cols-4">
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function SportCard({ sport, config, accountId }: {
  sport: NativeSport;
  config: SportsSectionConfig;
  accountId: string;
}) {
  const tokens = useNativeTokens();
  const { i18n } = useTranslation();
  const visual = config.sports[sport.id];
  const name = pickLocalized(sport.name, sport.name_alt ?? undefined, i18n.language) ?? sport.name;
  const sportVisual = resolveHomeSportVisual(sport.slug, sport.icon, homeAssetUrl(accountId, visual?.imageKey), visual?.icon);

  return (
    <Link href={`/book?activity=${encodeURIComponent(sport.slug)}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0, width: 92, textDecoration: 'none', color: tokens.foreground }}>
      <div style={{ position: 'relative', width: 84, height: 84, borderRadius: '50%', overflow: 'hidden', backgroundColor: tokens.line, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.muted }}>
        <SportVisualView visual={sportVisual} size={84} glyphSize={32} strokeWidth={1.2} />
      </div>
      <span style={{ fontFamily: tokens.fontDisplay, fontSize: 14, lineHeight: 1.2, textAlign: 'center', width: '100%', overflowWrap: 'anywhere' }}>{name}</span>
    </Link>
  );
}
